import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { format, addDays, startOfDay, endOfDay, setHours, setMinutes, isWeekend, parseISO, isBefore, isAfter, addHours } from 'date-fns';
import { supabase } from '../../../../lib/supabase';
import { saveMentorAvailability, getMentorAvailability, AvailabilitySlot } from '../../data/mentorAvailability';

interface TimeSlot {
  id: string; // Add unique ID for each slot
  start: Date;
  end: Date;
  selected: boolean;
  type: 'available' | 'busy';
}

interface MentorInfo {
  firstName: string;
  lastName: string;
  email: string;
  id: string;
}

const DAYS_OUT = 14

export function MentorAvailability() {
  const { sessionId, mentorId } = useParams();
  const navigate = useNavigate();
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mentorInfo, setMentorInfo] = useState<MentorInfo | null>(null);

  // Create a hash map of slots for efficient lookup
  const slotsMap = useMemo(() => {
    const map = new Map<string, number>();
    slots.forEach((slot, index) => {
      map.set(slot.id, index);
    });
    return map;
  }, [slots]);

  // Load mentor information from localStorage
  useEffect(() => {
    if (sessionId) {
      const mentorStorageKey = `findtime-session-${sessionId}-mentor`;
      const storedMentorJson = localStorage.getItem(mentorStorageKey);
      
      if (storedMentorJson) {
        try {
          const parsedMentor = JSON.parse(storedMentorJson);
          console.log('Found stored mentor info:', parsedMentor);
          setMentorInfo(parsedMentor);
        } catch (err) {
          console.error('Error parsing stored mentor info:', err);
        }
      }
    }
  }, [sessionId]);

  const generateAllSlots = (
    vincerEvents: { start: string; end: string; eventDetails: any }[],
    busyTimes: { start: string; end: string }[]
  ) => {
    const allSlots: TimeSlot[] = [];
    
    // For each VINCER event, generate 1-hour slots
    vincerEvents.forEach((event) => {
      const eventStart = parseISO(event.start);
      const eventEnd = parseISO(event.end);
      
      // Generate slots for each hour within the event
      let currentSlotStart = eventStart;
      
      while (isBefore(currentSlotStart, eventEnd)) {
        const slotEnd = addHours(currentSlotStart, 1);
        
        // Only add the slot if it doesn't extend beyond the event end
        if (isBefore(slotEnd, eventEnd) || slotEnd.getTime() === eventEnd.getTime()) {
          // Check if this slot overlaps with any busy times
          const isSlotBusy = busyTimes.some(busyTime => {
            const busyStart = parseISO(busyTime.start);
            const busyEnd = parseISO(busyTime.end);
            return (
              (isBefore(currentSlotStart, busyEnd) && isAfter(slotEnd, busyStart)) ||
              (isBefore(busyStart, slotEnd) && isAfter(busyEnd, currentSlotStart))
            );
          });

          if (!isSlotBusy) {
            const slotId = `${currentSlotStart.toISOString()}-${slotEnd.toISOString()}-available`;
            
            allSlots.push({
              id: slotId,
              start: currentSlotStart,
              end: slotEnd,
              selected: false,
              type: 'available'
            });
          }
        }
        
        currentSlotStart = slotEnd;
      }
    });

    console.log("Generated slots from VINCER events: ", allSlots);
    
    return allSlots;
  };

  const fetchCalendarData = async () => {
    try {
      const accessToken = sessionStorage.getItem('googleAccessToken');
      
      if (!accessToken) {
        throw new Error('No Google credentials found');
      }

      console.log("Availability Access Token: ", accessToken)
      const tomorrow = startOfDay(addDays(new Date(), 1));
      const twoWeeksOut = endOfDay(addDays(tomorrow, DAYS_OUT));
      
      // Get all events for the time period
      const queryParams = new URLSearchParams({
        timeMin: tomorrow.toISOString(),
        timeMax: twoWeeksOut.toISOString(),
        singleEvents: 'true',
        orderBy: 'startTime'
      });

      const eventsResponse = await fetch(`https://www.googleapis.com/calendar/v3/calendars/primary/events?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        }
      });

      if (!eventsResponse.ok) {
        throw new Error(`Events API error: ${eventsResponse.statusText}`);
      }

      const eventsData = await eventsResponse.json();
      console.log('All events:', eventsData.items);

      // Filter events to only include those with VINCER in the title
      const vincerEvents = eventsData.items.filter((event: any) => 
        event.summary?.includes('VINCER')
      );

      console.log('VINCER events:', vincerEvents);

      // Get busy times by filtering out VINCER events
      const busyTimes = eventsData.items
        .filter((event: any) => !event.summary?.includes('VINCER'))
        .map((event: any) => ({
          start: event.start.dateTime || event.start.date,
          end: event.end.dateTime || event.end.date
        }));

      console.log('Busy times (non-VINCER events):', busyTimes);

      // Convert events to the format expected by generateAllSlots
      const vincerEventSlots = vincerEvents.map((event: any) => ({
        start: event.start.dateTime || event.start.date,
        end: event.end.dateTime || event.end.date,
        eventDetails: {
          summary: event.summary,
          attendees: event.attendees,
          description: event.description,
          location: event.location
        }
      }));

      const allSlots = generateAllSlots(vincerEventSlots, busyTimes);
      
      if (allSlots.length === 0) {
        // If no slots were generated, we need to create some default slots
        // This happens when there are no VINCER events in the calendar
        const defaultSlots = generateDefaultSlots();
        setSlots(defaultSlots);
      } else {
        setSlots(allSlots);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching calendar data:', err);
      setError('Failed to fetch calendar data. Please try again.');
      setLoading(false);
    }
  };

  // Generate default slots when no VINCER events are found
  const generateDefaultSlots = () => {
    const defaultSlots: TimeSlot[] = [];
    const startDate = startOfDay(addDays(new Date(), 1)); // Start from tomorrow
    
    // Generate slots for the next 14 days
    for (let i = 0; i < DAYS_OUT; i++) {
      const currentDate = addDays(startDate, i);
      
      // Skip weekends
      if (isWeekend(currentDate)) continue;
      
      // Generate slots for business hours (9 AM to 5 PM)
      for (let hour = 9; hour < 17; hour++) {
        const slotStart = setHours(setMinutes(currentDate, 0), hour);
        const slotEnd = setHours(setMinutes(currentDate, 0), hour + 1);
        
        defaultSlots.push({
          id: `${slotStart.toISOString()}-${slotEnd.toISOString()}-available`,
          start: slotStart,
          end: slotEnd,
          selected: false,
          type: 'available'
        });
      }
    }
    
    return defaultSlots;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Get the current user's session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          setError('You must be logged in to view this page');
          
          // If we're in the mentor dashboard, redirect to login
          if (!sessionId && !mentorId) {
            navigate('/login');
          }
          return;
        }
        
        // Check if we have a Google access token
        const accessToken = sessionStorage.getItem('googleAccessToken');
        
        if (!accessToken) {
          // If we're in the mentor dashboard, redirect to calendar connect
          if (!sessionId && !mentorId) {
            navigate('/mentor/calendar/connect');
          } else {
            setError('No Google credentials found. Please reconnect your calendar.');
          }
          return;
        }
        
        // Fetch calendar data to get available slots
        await fetchCalendarData();
        
        // Get the current user's ID
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          throw new Error('User not authenticated');
        }
        
        // Load saved availability
        await loadSavedAvailability(user.id);
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [sessionId, mentorId, navigate]);

  // Updated toggleSlot to use the slot ID instead of index
  const toggleSlot = (slotId: string) => {
    const index = slotsMap.get(slotId);
    
    if (index !== undefined) {
      setSlots(currentSlots => 
        currentSlots.map((slot, i) => 
          i === index ? { ...slot, selected: !slot.selected } : slot
        )
      );
    }
  };

  const handleRelinkCalendar = () => {
    // Clear the existing token
    sessionStorage.removeItem('googleAccessToken');
    // Navigate to the calendar connect page
    navigate('/mentor/calendar/connect');
  };

  const handleSubmit = async () => {
    const selectedSlots = slots.filter(slot => slot.selected && slot.type === 'available');
    console.log('Selected slots:', selectedSlots);
    
    if (selectedSlots.length === 0) {
      setError('Please select at least one time slot');
      return;
    }
    
    setLoading(true);
    
    try {
      // Format the availability data for the database
      const availabilityData: AvailabilitySlot[] = selectedSlots.map(slot => ({
        start: slot.start.toISOString(),
        end: slot.end.toISOString()
      }));
      
      // Get the current user's ID
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Save the availability data using our data access module
      // We pass the user ID, and the function will look up the mentor UUID
      const result = await saveMentorAvailability(user.id, availabilityData);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to save availability');
      }
      
      // Instead of clearing selections, reload the saved availability
      await loadSavedAvailability(user.id);
      
    } catch (err) {
      console.error('Error saving availability:', err);
      setError(err instanceof Error ? err.message : 'Failed to save availability');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to load saved availability
  const loadSavedAvailability = async (userId: string) => {
    try {
      // First, get the mentor UUID from the mentor table
      const { data: mentorData, error: mentorError } = await supabase
        .from('mentor')
        .select('uuid')
        .eq('user_uuid', userId)
        .single();
      
      if (mentorError) {
        console.error('Error fetching mentor data:', mentorError);
        return;
      }
      
      if (!mentorData) {
        console.error('No mentor record found for this user');
        return;
      }
      
      const mentorUuid = mentorData.uuid;
      
      // If we have a mentorId from URL params, use that instead
      const targetMentorId = mentorId || mentorUuid;
      
      // Fetch the mentor's availability
      const result = await getMentorAvailability(targetMentorId);
      
      if (result.success && result.data) {
        console.log('Loaded existing availability:', result.data);
        
        // Convert the stored availability data to our TimeSlot format
        const existingSlots = result.data.availability_data.map(slot => ({
          id: `${slot.start}-${slot.end}-available`,
          start: parseISO(slot.start),
          end: parseISO(slot.end),
          selected: true,
          type: 'available' as const
        }));
        
        // Mark these slots as selected in our slots array
        setSlots(currentSlots => 
          currentSlots.map(slot => {
            // Check if this slot matches any of the existing slots
            const matchingSlot = existingSlots.find(
              existing => 
                existing.start.getTime() === slot.start.getTime() && 
                existing.end.getTime() === slot.end.getTime()
            );
            
            return matchingSlot ? { ...slot, selected: true } : slot;
          })
        );
      }
    } catch (err) {
      console.error('Error loading saved availability:', err);
    }
  };

  // Group slots by date for better organization
  const slotsByDate = slots.reduce<Record<string, TimeSlot[]>>((acc, slot) => {
    const date = format(slot.start, 'yyyy-MM-dd');
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(slot);
    return acc;
  }, {});

  if (slotsByDate) {
    console.log("Slots by date: ", slotsByDate)
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Your Calendar Availability</h1>
          {mentorInfo && (
            <p className="text-gray-600">Hello, {mentorInfo.firstName} {mentorInfo.lastName}</p>
          )}
        </div>
        
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vincer-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your availability...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={handleRelinkCalendar}
              className="bg-vincer-500 text-white font-medium px-6 py-3 rounded-lg hover:bg-vincer-400 transition-colors"
            >
              Relink Calendar
            </button>
          </div>
        ) : slots.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">No available time slots found.</p>
            <p className="text-sm text-gray-500 mb-6">You need to connect your calendar to see your availability.</p>
            <button
              onClick={() => navigate('/mentor/calendar/connect')}
              className="bg-vincer-500 text-white font-medium px-6 py-3 rounded-lg hover:bg-vincer-400 transition-colors"
            >
              Connect Your Calendar
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(slotsByDate).map(([date, dateSlots]) => (
              <div key={date} className="border rounded-lg p-4">
                <h2 className="text-lg font-semibold mb-4">
                  {format(parseISO(date), 'EEEE, MMMM d, yyyy')}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {[...dateSlots].sort((a, b) => a.start.getTime() - b.start.getTime()).map((slot) => (
                    <button
                      key={slot.id}
                      onClick={() => toggleSlot(slot.id)}
                      className={`
                        flex items-center justify-between
                        px-4 py-2 rounded-lg text-sm
                        transition-colors duration-200
                        cursor-pointer
                        ${slot.selected 
                          ? 'bg-vincer-500 text-white'
                          : 'bg-gray-50 text-gray-900 hover:bg-gray-100'}
                      `}
                    >
                      <span>{format(slot.start, 'h:mm a')}</span>
                      {slot.selected && <X className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && slots.length > 0 && (
          <div className="mt-8 flex justify-end">
            <button
              onClick={handleSubmit}
              className="bg-vincer-500 text-white font-medium px-6 py-3 rounded-lg hover:bg-vincer-400 transition-colors"
            >
              Submit Availability
            </button>
          </div>
        )}
      </div>
    </div>
  );
}